import { useEffect, useLayoutEffect, useRef } from 'react';

/**
 * The rail keeps its scroll position. SURF-4829.
 *
 * Moving between CMS screens is a client-side route change and the rail is never remounted,
 * so it already kept its place then. What lost it is a full page load, and a lower item is
 * exactly where those come from: Log Out; a tenant or subtenant switch, which reloads on
 * purpose; and a browser reload. Each of them rebuilt the rail at the top, so an admin working
 * in Advanced Settings scrolled back down after every one. Change Password was one too until
 * SURF-4828 (`href: '/changePassword'`, whose Cancel went back to `/`); it is now a screen
 * inside the frame, so opening and leaving it no longer reloads anything.
 *
 * The position goes in `sessionStorage`, as table state does (`usePageTable`): it belongs to
 * this tab and should not follow the admin into a new one.
 */
export const RAIL_SCROLL_KEY = 'cms.rail.scrollTop';

/** `window.sessionStorage`, or null where reading it throws (blocked storage, a sandbox). */
const sessionStore = () => {
  try {
    return globalThis.sessionStorage ?? null;
  } catch {
    return null;
  }
};

/** The saved position, or null if there is none or it is not a usable number. */
export const readRailScroll = (storage = sessionStore()) => {
  try {
    const raw = storage?.getItem(RAIL_SCROLL_KEY);
    if (raw == null || raw === '') return null;
    const top = Number(raw);
    return Number.isFinite(top) && top >= 0 ? top : null;
  } catch {
    return null;
  }
};

export const writeRailScroll = (top, storage = sessionStore()) => {
  try {
    storage?.setItem(RAIL_SCROLL_KEY, String(Math.max(0, Math.round(top))));
  } catch {
    // Full or blocked storage. The rail then starts at the top after a reload, as it used to.
  }
};

/**
 * The `scrollTop` that brings `item` fully into `view` by moving as little as possible, or
 * null when it is already fully visible. Both are `{ top, bottom }` viewport rectangles.
 *
 * This is `scrollIntoView({ block: 'nearest' })` confined to the rail. The real thing also
 * scrolls every scrollable ancestor, and `#wrapper` is one (`overflow: hidden` still
 * scrolls programmatically), so it could shift the whole frame.
 */
export const revealScrollTop = (item, view, scrollTop) => {
  if (item.top < view.top) return Math.max(0, scrollTop - (view.top - item.top));
  if (item.bottom > view.bottom) {
    // An item taller than the rail shows its top rather than its bottom.
    const down = Math.min(item.bottom - view.bottom, item.top - view.top);
    return scrollTop + down;
  }
  return null;
};

/**
 * `useLayoutEffect` in the browser, `useEffect` where there is no DOM, as in `BackButton`.
 * The CMS only renders in the browser, but the rail's tests render it with
 * `react-dom/server`, where React 18 warns about every layout effect it cannot run.
 */
const useIsomorphicLayoutEffect = typeof document === 'undefined' ? useEffect : useLayoutEffect;

/**
 * What to bring into view for a hidden item: the item and its group's caption together when
 * both fit, so an item revealed at the top of the rail does not read as part of the group
 * above it (the logo takes the rail to NavBar and should show "Menus" too). Otherwise, or in
 * an unlabelled group, the item alone.
 */
export const revealRect = (item, caption, view) =>
  caption && item.bottom - caption.top <= view.bottom - view.top
    ? { top: Math.min(caption.top, item.top), bottom: item.bottom }
    : item;

/** Scroll `nav` just enough to show its highlighted item, if that item is hidden. */
const revealSelected = (nav) => {
  const item = nav.querySelector('[aria-current="page"]');
  if (!item) return;
  const box = item.getBoundingClientRect();
  const view = nav.getBoundingClientRect();
  // A visible item leaves the rail alone, even with its caption scrolled away.
  if (revealScrollTop(box, view, nav.scrollTop) == null) return;
  const caption = item.closest('.nav-group')?.querySelector('.nav-group-label');
  const next = revealScrollTop(
    revealRect(box, caption?.getBoundingClientRect(), view),
    view,
    nav.scrollTop
  );
  if (next != null) nav.scrollTop = next;
};

/**
 * What the rail does when it mounts, which is after every full page load.
 *
 * The saved position comes back first, and then the highlighted item is brought into view,
 * except on the home screen. Home is not a screen the admin asked for: it is where the CMS
 * lands after a login and a tenant switch (and, before SURF-4828, after Cancel on the
 * standalone Change Password page), and its item is at the top of the rail, so revealing it
 * would scroll straight back to the top, which is the reported defect. Any other screen (a reload, a pasted link) shows its item. With nothing
 * saved (a new tab, an emailed link) the item is revealed.
 */
export const mountScroll = (saved, selected) => ({
  scrollTop: saved,
  reveal: saved == null || !selected?.isHomePage,
});

/**
 * Wire the rail's scroll container: restore its position on mount (see `mountScroll`), save
 * it as it scrolls, and bring the highlighted item into view when the highlight moves. A
 * rail click never scrolls, because the item clicked is already visible, but a link inside
 * a page, the logo, or Back can land on an item scrolled out of sight.
 *
 * `useLayoutEffect` so the restored position is in place before the first paint and the
 * rail does not visibly jump. `selected` is compared by identity, like the highlight.
 */
export const useRailScroll = (ref, selected) => {
  // The highlight the rail was last scrolled for. A ref rather than a mount flag, so an
  // effect that runs twice for the same highlight (StrictMode) cannot scroll a second time.
  const shown = useRef(selected);

  // Mount only: the listener reads the live position, and the highlight has its own effect.
  useIsomorphicLayoutEffect(() => {
    const nav = ref.current;
    if (!nav) return undefined;
    const { scrollTop, reveal } = mountScroll(readRailScroll(), shown.current);
    if (scrollTop != null) nav.scrollTop = scrollTop;
    if (reveal) revealSelected(nav);
    const save = () => writeRailScroll(nav.scrollTop);
    nav.addEventListener('scroll', save, { passive: true });
    return () => nav.removeEventListener('scroll', save);
  }, [ref]);

  useEffect(() => {
    if (shown.current === selected) return;
    shown.current = selected;
    if (ref.current) revealSelected(ref.current);
  }, [ref, selected]);
};
